/**
 * Geographic utilities for ZIP code resolution
 *
 * Provides frontend ZIP-to-city conversion using the zipcodes npm library
 * Also provides city name search for autocomplete (helps find small towns)
 */

import zipdb from 'zipcodes'

const ZIP_RE = /^\d{5}$/

export function isZip(v) {
  return !!v && ZIP_RE.test(v.trim())
}

export function resolveCityFromZip(zip) {
  const z = zip.trim()

  if (!isZip(z)) {
    return null
  }

  try {
    const info = zipdb.lookup(z)
    if (!info || !info.city) {
      return null
    }

    return {
      city: info.city,
      state: info.state || ''
    }
  } catch (error) {
    console.warn('Error resolving ZIP code:', z, error)
    return null
  }
}

/**
 * Search for cities by partial name match
 * Returns unique city/state combinations that match the query
 * Useful for finding small towns that Google Places doesn't include
 *
 * @param {string} query - Partial city name to search (e.g., "West Cr")
 * @param {number} limit - Maximum results to return (default 10)
 * @returns {Array<{city: string, state: string, zip: string}>}
 */
export function searchCitiesByName(query, limit = 10) {
  if (!query || query.length < 2) {
    return []
  }

  const searchTerm = query.toLowerCase().trim()
  const results = []
  const seen = new Set() // Track unique city+state combinations

  try {
    // Access the raw codes data from zipcodes library
    const allCodes = zipdb.codes

    for (const zip in allCodes) {
      const info = allCodes[zip]

      // Skip non-US entries or entries without city
      if (!info || !info.city || info.country !== 'US') continue

      const cityLower = info.city.toLowerCase()

      // Check if city starts with the search term (prioritize) or contains it
      if (cityLower.startsWith(searchTerm)) {
        const key = `${info.city}|${info.state}`

        if (!seen.has(key)) {
          seen.add(key)
          results.push({
            city: info.city,
            state: info.state,
            zip: zip,
            latitude: info.latitude,
            longitude: info.longitude,
            // Mark as exact start match for sorting
            _startsWithMatch: true
          })
        }
      }
    }

    // If we don't have enough results, also search for "contains" matches
    if (results.length < limit) {
      for (const zip in allCodes) {
        const info = allCodes[zip]

        if (!info || !info.city || info.country !== 'US') continue

        const cityLower = info.city.toLowerCase()
        const key = `${info.city}|${info.state}`

        // Only add if it contains (not starts with) and not already added
        if (!seen.has(key) && cityLower.includes(searchTerm) && !cityLower.startsWith(searchTerm)) {
          seen.add(key)
          results.push({
            city: info.city,
            state: info.state,
            zip: zip,
            latitude: info.latitude,
            longitude: info.longitude,
            _startsWithMatch: false
          })
        }

        if (results.length >= limit * 2) break // Get enough for sorting
      }
    }

    // Sort: startsWith matches first, then alphabetically
    results.sort((a, b) => {
      if (a._startsWithMatch && !b._startsWithMatch) return -1
      if (!a._startsWithMatch && b._startsWithMatch) return 1
      return a.city.localeCompare(b.city)
    })

    // Clean up internal sorting flag and limit results
    return results.slice(0, limit).map(({ _startsWithMatch, ...rest }) => rest)
  } catch (error) {
    console.warn('Error searching cities:', error)
    return []
  }
}